import { isEmpty, noop } from 'lodash'
import {
  columnTypesConst,
  ExpandRow,
  filterOperatorValues,
  getFilterOperators,
  GetHeaderColumnPropsType,
  getInitialFilterOperator,
  SearchCriteria,
  SORT_KEYS,
  TABLE_ACTION_TYPE,
  TableColumnType,
  TableDataColumnsPropsTypes,
  tableFilterType,
  TableHeaderColumnsPropsTypes,
  TableRowPropsTypes,
  widthClasses,
} from '../../utils'
import ToggleButton from '../ToggleButton'
import Tooltip from '../Tooltip'
import { SvgDisableSortArrow, SvgSortArrowUser } from '../../assets/images'
import React, { useEffect, useRef, useState } from 'react'
import { TableActions } from './TableActions'
import TableColumnFilter from './TableColumnFilter'
import Popover from '../Popover'
import { FilterOperatorsModal } from './custom-table-filters/FilterModal'
import SvgDropdownArrowGrey from '../../assets/images/components/SvgDropdownArrowGrey'
import {
  FilterHeaderPropsType,
  FilterOperatorsType,
} from '../../utils/types/tableFilterTypes'
import { StringFilterInput } from './custom-table-filters/StringFilterInput'
import { NumericFilterInput } from './custom-table-filters/NumericFilterInput'
import { BooleanFilterInput } from './custom-table-filters/BooleanFilterInput'
import { DropdownFilterInput } from './custom-table-filters/DropdownFilterInput'
import { DateFilterInput } from './custom-table-filters/DateFilterInput'
import { DateTimeFilterInput } from './custom-table-filters/DateTimeFilterInput'
import { ShortcutKeys } from '../../utils/tab-indexing-contants'
import { INACTIVE_LOOKUP_TOOLTIPS } from '../../utils/constants'
import {
  getInactiveLookupKeysFromRow,
  parseInactiveLookupTooltips,
} from '../../utils/inactiveLookup.utils'

const DISABLED_CELL_TEXT_CLASS = 'text-accent-300'

// Finds the inactive lookup path that matches a table column accessor.
const getMatchingInactiveLookupKey = (
  row: unknown,
  accessor: string | number | symbol | undefined
): string | undefined => {
  const inactiveKeys = getInactiveLookupKeysFromRow(row)
  if (!inactiveKeys?.length || !accessor) return undefined

  const accessorStr = String(accessor)
  return inactiveKeys
    ?.filter((key) => accessorStr === key || accessorStr.startsWith(`${key}.`))
    ?.sort((a, b) => b.length - a.length)?.[0]
}

// Returns true when a column should be disabled due to an inactive lookup.
const isColumnDisabledByInactiveLookup = (
  row: unknown,
  accessor: string | number | symbol | undefined
): boolean => !!getMatchingInactiveLookupKey(row, accessor)

// Returns the disabled tooltip message for an inactive lookup column.
const getInactiveLookupTooltip = (
  row: unknown,
  accessor: string | number | symbol | undefined
): string | undefined => {
  const matchedKey = getMatchingInactiveLookupKey(row, accessor)
  if (!matchedKey) return undefined

  const tooltips = parseInactiveLookupTooltips(
    (row as Record<string, unknown> | undefined)?.[INACTIVE_LOOKUP_TOOLTIPS]
  )

  return tooltips?.[matchedKey]
}

// Picks the tooltip text shown for a table cell.
const getCellTooltipContent = (
  fieldValue: unknown,
  inactiveLookupTooltip?: string
): string => {
  if (inactiveLookupTooltip) return inactiveLookupTooltip
  if (Array.isArray(fieldValue)) return fieldValue?.join(', ') ?? ''
  if (fieldValue === undefined || fieldValue === null) return ''
  return String(fieldValue)
}

export const TableDataColumn = <T,>({
  column,
  row,
  rowIndex,
  onColumnClick = noop,
  isDisabled = false,
  tabIndex,
}: TableDataColumnsPropsTypes<T>) => {
  const fieldValue = row?.[column?.accessor]
  const isInactiveLookupColumn = isColumnDisabledByInactiveLookup(
    row,
    column?.accessor
  )
  const effectiveDisabled = isDisabled || isInactiveLookupColumn
  const disabledTextClass = effectiveDisabled ? DISABLED_CELL_TEXT_CLASS : ''
  const inactiveLookupTooltip = getInactiveLookupTooltip(row, column?.accessor)
  const cellTooltipContent = getCellTooltipContent(
    fieldValue,
    isInactiveLookupColumn ? inactiveLookupTooltip : undefined
  )
  switch (column?.type) {
    case columnTypesConst?.CHECKBOX:
      return (
        <div className="flex justify-center items-center">
          <input
            type="checkbox"
            className="h-[1.125rem] w-[1.125rem] accent-secondary-500"
            checked={!!fieldValue}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              onColumnClick(false, column?.accessor, row, rowIndex)
            }
            disabled={
              effectiveDisabled ? !column?.isEnabledOnDisabledRow : false
            }
            data-qa-id={`checkbox_${rowIndex}`}
            tabIndex={tabIndex}
          />
        </div>
      )
    case columnTypesConst?.TOGGLE_BUTTON:
      return column?.isHeader ? (
        <span className="w-full block truncate">{column?.header}</span>
      ) : column?.isVisibleInHeader ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ToggleButton
            name={column?.header}
            checked={!!fieldValue}
            onChange={() =>
              onColumnClick(false, column?.accessor, row, rowIndex)
            }
            tabIndex={tabIndex}
          />
        </div>
      ) : null
    case columnTypesConst?.HYPERLINK:
      return (
        <Tooltip content={cellTooltipContent} position="top" className="w-fit">
          <span
            className={`w-full block truncate text-xs font-light ${
              effectiveDisabled
                ? `${DISABLED_CELL_TEXT_CLASS} cursor-not-allowed`
                : 'text-secondary-500'
            }`}
            onClick={() => {
              if (!effectiveDisabled) {
                onColumnClick(false, column?.accessor, row, rowIndex)
              }
            }}
            tabIndex={
              ((fieldValue as React.ReactNode) || '--') === '--'
                ? -1
                : tabIndex ?? 0
            }
            role="button"
            onKeyDown={(e) => {
              if (
                e?.key === ShortcutKeys.Enter ||
                e?.key === ShortcutKeys.Space
              ) {
                e?.preventDefault()
                onColumnClick(false, column?.accessor, row, rowIndex)
              }
            }}
          >
            {fieldValue === 0 ? '0' : (fieldValue as React.ReactNode) || '--'}
          </span>
        </Tooltip>
      )
    case columnTypesConst?.IMAGE:
      return (
        <span className="inline-block ">
          <img
            alt={'LOGO'}
            src={fieldValue as string}
            className="h-8 max-w-[4rem] w-auto object-cover"
          />
        </span>
      )
    case columnTypesConst?.COMPONENT:
      return column?.renderComponent &&
        typeof column.renderComponent === 'function' ? (
        <div className="custom-cell-render">
          {column.renderComponent(
            row,
            rowIndex,
            onColumnClick,
            effectiveDisabled,
            tabIndex
          )}
        </div>
      ) : null
    case columnTypesConst?.MULTI_LINE_TEXT:
      return (
        <Tooltip
          content={cellTooltipContent}
          position="top"
          className={
            (cellTooltipContent?.length ?? 0) < 20 ? 'w-fit' : 'max-w-96'
          }
        >
          <div className="flex justify-center items-center">
            <div className={`line-clamp-3 ${disabledTextClass}`}>
              {fieldValue as string}
            </div>
          </div>
        </Tooltip>
      )
    case columnTypesConst?.RADIO:
      return (
        <div className="flex justify-center items-center">
          <input
            type="radio"
            name={column?.name}
            className="h-[1.125rem] w-[1.125rem] accent-secondary-500"
            checked={!!fieldValue}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              onColumnClick(false, column?.accessor, row, rowIndex)
            }
            disabled={
              effectiveDisabled ? !column?.isEnabledOnDisabledRow : false
            }
            tabIndex={tabIndex}
          />
        </div>
      )
    default:
      return Array.isArray(fieldValue) ? (
        <Tooltip
          content={cellTooltipContent}
          position="top"
          className={'max-w-96'}
        >
          <span
            className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
          >
            {(fieldValue.join(', ') as string) || '--'}
          </span>
        </Tooltip>
      ) : fieldValue !== undefined && fieldValue !== null ? (
        column.hideToolTip ? (
          isInactiveLookupColumn && inactiveLookupTooltip ? (
            <Tooltip
              content={inactiveLookupTooltip}
              position="top"
              className="w-fit"
            >
              <span
                className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
              >
                {(fieldValue as React.ReactNode) || '--'}
              </span>
            </Tooltip>
          ) : (
            <span
              className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
            >
              {(fieldValue as React.ReactNode) || '--'}
            </span>
          )
        ) : (
          <Tooltip
            content={cellTooltipContent}
            position="top"
            className={
              (cellTooltipContent?.length ?? 0) < 20 ? 'w-fit' : 'max-w-96'
            }
          >
            <span
              className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
            >
              {fieldValue as React.ReactNode}
            </span>
          </Tooltip>
        )
      ) : isInactiveLookupColumn && inactiveLookupTooltip ? (
        <Tooltip
          content={inactiveLookupTooltip}
          position="top"
          className="w-fit"
        >
          <span
            className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
          >
            {'--'}
          </span>
        </Tooltip>
      ) : (
        <span
          className={`font-light text-xs w-full truncate block ${disabledTextClass}`}
        >
          {'--'}
        </span>
      )
  }
}

export const FilterHeader = <T,>({
  columnData,
  tableName,
  handleFilterChange = noop,
  filterObject,
  filterOperator,
  columnHeaderRef,
  innerInputRef,
  tabIndex,
}: FilterHeaderPropsType<T>) => {
  const search_key =
    columnData?.filterData?.convertToUppercase === false
      ? (columnData?.accessor as string)
      : (columnData?.accessor as string)?.toUpperCase()

  const renderFilter = () => {
    switch (columnData?.filterData?.dataType) {
      case tableFilterType.STRING:
      case tableFilterType.STRING_DYNAMIC:
        return (
          <StringFilterInput
            columnData={columnData}
            handleFilterChange={handleFilterChange}
            filterObject={filterObject}
            tableName={tableName}
            dataQaId={`${tableName}_${search_key}_string_filter`}
            filterOperator={filterOperator}
            columnHeaderRef={columnHeaderRef}
            innerInputRef={innerInputRef}
            tabIndex={tabIndex}
          />
        )
      case tableFilterType.NUMBER:
        return (
          <NumericFilterInput
            filterObject={filterObject}
            tableName={tableName}
            search_key={search_key}
            handleFilterChange={handleFilterChange}
            dataQaId={`${tableName}_${search_key}_numeric_filter`}
            filterOperator={filterOperator}
            columnHeaderRef={columnHeaderRef}
            innerInputRef={innerInputRef}
            tabIndex={tabIndex}
          />
        )
      case tableFilterType.BOOLEAN:
        return (
          <BooleanFilterInput
            columnData={columnData}
            handleFilterChange={handleFilterChange}
            search_key={search_key}
            dataQaId={`${tableName}_${search_key}_boolean_filter`}
            filterOperator={filterOperator}
            filterObject={filterObject}
            tabIndex={tabIndex}
          />
        )
      case tableFilterType.DROPDOWN:
      case tableFilterType.MULTI_SELECT_DYNAMIC:
        return (
          <DropdownFilterInput
            columnData={columnData}
            handleFilterChange={handleFilterChange}
            filterObject={filterObject}
            dataQaId={`${tableName}_${search_key}_dropdown_filter`}
            filterOperator={filterOperator}
            columnHeaderRef={columnHeaderRef}
            innerInputRef={innerInputRef}
            tabIndex={tabIndex}
          />
        )
      case tableFilterType.DATE:
        return (
          <DateFilterInput
            search_key={search_key}
            handleFilterChange={handleFilterChange}
            tableName={tableName}
            filterObject={filterObject}
            dataQaId={`${tableName}_${search_key}_date_filter`}
            filterOperator={filterOperator}
            columnHeaderRef={columnHeaderRef}
            innerInputRef={innerInputRef}
            tabIndex={tabIndex}
          />
        )
      case tableFilterType.DATE_TIME:
      case tableFilterType.TIME:
      case tableFilterType.DATE_TIME_DYNAMIC:
        return (
          <DateTimeFilterInput
            tableName={tableName}
            columnData={columnData}
            handleFilterChange={handleFilterChange}
            filterObject={filterObject}
            dataQaId={`${tableName}_${search_key}_datetime_filter`}
            filterOperator={filterOperator}
            columnHeaderRef={columnHeaderRef}
            innerInputRef={innerInputRef}
            tabIndex={tabIndex}
          />
        )
      default:
        return null
    }
  }
  return (
    <div className="flex p-1 gap-1 border-t border-accent-0 min-h-[2.875rem]">
      {columnData?.filterData ? renderFilter() : null}
    </div>
  )
}

export const GetHeaderColumn = <T,>({
  column,
  sort,
  onColumnClick,
  isAsc,
  headerValues,
  toggleIsAsc,
  tableName = '',
  onFilterChange,
  filterObject,
  columnHeaderRef,
  isFilterPresent,
  operatorRef,
  tabIndex,
  isAgGrid = false,
}: GetHeaderColumnPropsType<T>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [showFilterPopUp, setShowFilterPopUp] = useState<boolean>(false)
  const filterPopUpRef = useRef(false)
  const previousFocusedElement = useRef<Element | null>(null)
  const columnHeaderRefInner = useRef<null>(null)
  columnHeaderRef = columnHeaderRef ?? columnHeaderRefInner

  const getInitialFilterOperatorLabel = () => {
    if (!isEmpty(operatorRef?.current?.[column?.accessor.toString()]))
      return operatorRef?.current?.[column?.accessor.toString()]
    else if (
      filterObject?.search_criteria?.[column?.accessor.toString()]?.operator
    )
      return getInitialFilterOperator(
        column?.filterData?.dataType,
        filterObject.search_criteria[column?.accessor.toString()].operator
      )
    else return getInitialFilterOperator(column?.filterData?.dataType)
  }

  const [filterOperator, setFilterOperator] =
    useState<FilterOperatorsType | null>(getInitialFilterOperatorLabel())

  const innerInputRef = useRef<HTMLInputElement | null>(null)

  const handleFilterChange = (columnFilterData: SearchCriteria) => {
    if (
      !columnFilterData.search_value?.length &&
      filterObject &&
      filterObject.search_criteria?.[columnFilterData.search_key]
    ) {
      const operator =
        filterObject.search_criteria[columnFilterData.search_key].operator
      const nullOperators = [
        filterOperatorValues.IS_NULL,
        filterOperatorValues.IS_NOT_NULL,
        filterOperatorValues.IS_EMPTY_NULL,
        filterOperatorValues.IS_NOT_EMPTY_NOT_NULL,
      ]
      if (nullOperators.includes(operator)) {
        setFilterOperator(
          getInitialFilterOperator(column?.filterData?.dataType)
        )
        const currOperator = getInitialFilterOperator(
          column?.filterData?.dataType
        )
        if (operatorRef && currOperator) {
          operatorRef.current = {
            ...operatorRef.current,
            [column?.accessor]: currOperator,
          }
        }
      }
      const searchFilter = { ...filterObject.search_criteria }
      delete searchFilter[columnFilterData.search_key]
      onFilterChange?.({
        fields: null,
        search_criteria: searchFilter,
      })
    } else if (columnFilterData.search_value?.length)
      onFilterChange?.({
        fields: null,
        search_criteria: {
          ...filterObject?.search_criteria,
          [columnFilterData.search_key]: columnFilterData,
        },
      })
  }

  useEffect(() => {
    if (isAgGrid) return
    if (operatorRef) {
      if (filterOperator?.value) {
        operatorRef.current = {
          ...operatorRef.current,
          [column?.accessor]: {
            label: filterOperator.label,
            value: filterOperator.value,
          },
        }
      }
    }
  }, [filterOperator])

  useEffect(() => {
    if (isAgGrid) return
    if (
      filterPopUpRef.current &&
      !showFilterPopUp &&
      !column?.isFilterDisabled
    ) {
      setTimeout(() => {
        innerInputRef.current?.focus()
      }, 200)
    }
    filterPopUpRef.current = showFilterPopUp
  }, [showFilterPopUp])

  useEffect(() => {
    if (isAgGrid) return
    if (filterObject?.search_criteria?.[column?.accessor.toString()]) {
      const filterOperators = getFilterOperators(column?.filterData?.dataType)
      const requiredOperatorOption: FilterOperatorsType | null =
        filterOperators.find(
          (operator) =>
            operator.value ===
            filterObject?.search_criteria?.[column?.accessor.toString()]
              ?.operator
        ) ?? null
      const nullOperators = [
        filterOperatorValues.IS_NULL,
        filterOperatorValues.IS_NOT_NULL,
        filterOperatorValues.IS_EMPTY_NULL,
        filterOperatorValues.IS_NOT_EMPTY_NOT_NULL,
      ]

      if (requiredOperatorOption?.value) {
        if (nullOperators.includes(requiredOperatorOption?.value)) {
          setFilterOperator(requiredOperatorOption)
          if (operatorRef) {
            operatorRef.current = {
              ...operatorRef.current,
              [column?.accessor]: requiredOperatorOption,
            }
          }
        } else {
          setFilterOperator({
            label: requiredOperatorOption?.label,
            value:
              requiredOperatorOption?.value === 'LIKE'
                ? filterOperatorValues.CONTAINS
                : requiredOperatorOption?.value,
          })
        }
      } else {
        setFilterOperator(
          getInitialFilterOperator(column?.filterData?.dataType)
        )
      }
    } else {
      setFilterOperator(getInitialFilterOperator(column?.filterData?.dataType))
    }
  }, [
    filterObject?.search_criteria?.[column?.accessor.toString()]?.operator,
    filterObject?.search_criteria?.[column?.accessor.toString()]?.search_value,
  ])

  switch (column?.type) {
    case columnTypesConst?.CHECKBOX:
      return column?.isHeader ? (
        <span className="w-full block truncate font-light leading-4.5 text-xs">
          {column.header}
        </span>
      ) : column?.isVisibleInHeader ? (
        <div className="flex justify-center items-center p-3">
          <input
            type="checkbox"
            className="h-[1.125rem] w-[1.125rem] accent-secondary-500"
            checked={!!headerValues?.[column?.accessor]}
            onChange={(e) => {
              onColumnClick(true, column?.accessor)
              e?.stopPropagation()
            }}
            data-qa-id={`checkbox_bulk`}
          />
        </div>
      ) : null
    case columnTypesConst?.TOGGLE_BUTTON:
      return column?.isHeader ? (
        <span className="w-full block truncate font-normal leading-4.5 text-xs">
          {column?.header}
        </span>
      ) : column?.isVisibleInHeader ? (
        <ToggleButton
          name={column?.header}
          checked={!!headerValues?.[column?.accessor]}
          onChange={(e) => {
            onColumnClick(true, column?.accessor)
            e?.stopPropagation()
          }}
        />
      ) : null
    default:
      return column?.renderHeader ? (
        <div className="custom-header-render">
          {column.renderHeader(onColumnClick, headerValues)}
        </div>
      ) : (
        <>
          <div
            className={`w-full flex justify-start items-center font-normal leading-4.5 text-xs gap-1 ${
              isAgGrid ? '' : 'px-4'
            } py-2.5 ${isFilterPresent && !isAgGrid ? 'min-w-48' : ''}`}
          >
            <Tooltip
              content={column?.header}
              cssClassName="overflow-hidden text-ellipsis"
            >
              <span className="font-regular whitespace-nowrap">
                {column?.header}
              </span>
            </Tooltip>
            {column?.isSortable && !isAgGrid && (
              <div
                className={`transform transition-transform duration-200 ml-0.5 cursor-pointer`}
                onMouseEnter={() =>
                  (previousFocusedElement.current = document.activeElement)
                }
                onClick={(e) => {
                  e?.stopPropagation()
                  toggleIsAsc?.()
                  if (previousFocusedElement.current) {
                    ;(
                      previousFocusedElement.current as HTMLInputElement
                    ).focus()
                  }
                }}
                tabIndex={tabIndex ?? 0}
                onFocus={() => {
                  previousFocusedElement.current = document.activeElement
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === ShortcutKeys.Enter ||
                    e.key === ShortcutKeys.Space
                  ) {
                    e?.stopPropagation()
                    e.preventDefault()
                    toggleIsAsc?.()
                    if (previousFocusedElement.current) {
                      ;(
                        previousFocusedElement.current as HTMLInputElement
                      ).focus()
                    }
                  }
                }}
              >
                {sort && sort?.key === column.accessor ? (
                  isAsc === 'asc' ? (
                    <SvgSortArrowUser />
                  ) : isAsc === 'desc' ? (
                    <SvgSortArrowUser className="rotate-180" />
                  ) : (
                    <SvgDisableSortArrow fill={'#5E5E5E'} />
                  )
                ) : (
                  <SvgDisableSortArrow fill={'#5E5E5E'} />
                )}
              </div>
            )}

            {column?.filterData && !column?.isFilterDisabled && !isAgGrid && (
              <span className="pl-2 ml-auto relative inline-block">
                {column?.filterData?.disableOperatorDropdown ? (
                  <span className="font-primary font-light text-xs/4.5 truncate">
                    {filterOperator?.label}
                  </span>
                ) : (
                  <div
                    onClick={() => setShowFilterPopUp((prev) => !prev)}
                    ref={inputRef}
                    className="inline-flex items-center gap-1 cursor-pointer"
                    tabIndex={tabIndex ?? 0}
                    onKeyDown={(e) => {
                      if (
                        e.key === ShortcutKeys.Enter ||
                        e.key === ShortcutKeys.Space
                      ) {
                        setShowFilterPopUp((prev) => !prev)
                        e.preventDefault()
                      }
                    }}
                  >
                    <span className="font-primary font-light text-xs/4.5 truncate">
                      {filterOperator?.label}
                    </span>
                    <SvgDropdownArrowGrey className="svg-path-fill-secondary" />
                  </div>
                )}

                {showFilterPopUp && (
                  <Popover
                    content={
                      <FilterOperatorsModal
                        toggleFilterPopUp={() =>
                          setShowFilterPopUp((prev) => !prev)
                        }
                        setFilterOperator={setFilterOperator}
                        filterOperator={filterOperator}
                        inputRef={innerInputRef}
                        operatorOptions={getFilterOperators(
                          column?.filterData?.dataType
                        )}
                      />
                    }
                    isOpen={showFilterPopUp}
                    showCloseButton={false}
                    cssClassName="mt-2 z-[10000] right-0"
                  />
                )}
              </span>
            )}
            {column?.isFilter && !isAgGrid ? (
              <TableColumnFilter
                column={column}
                tableName={tableName}
                onFilterChange={onFilterChange ?? noop}
                filter={filterObject || {}}
              />
            ) : null}
          </div>
          {isFilterPresent && !isAgGrid ? (
            <FilterHeader
              columnData={column}
              tableName={tableName}
              handleFilterChange={handleFilterChange}
              filterObject={filterObject}
              filterOperator={filterOperator}
              columnHeaderRef={columnHeaderRef}
              innerInputRef={innerInputRef}
              tabIndex={tabIndex}
            />
          ) : null}
        </>
      )
  }
}

export const TableHeaderColumns = <T,>({
  column,
  onColumnClick = noop,
  onSortClick = noop,
  headerValues,
  sort = null,
  tableName,
  onFilterChange = noop,
  filterObject,
  isFilterPresent,
  operatorRef,
  tabIndex,
  isAgGrid = false,
}: TableHeaderColumnsPropsTypes<T>) => {
  const [isAsc, setIsAsc] = useState<SORT_KEYS>(
    !isEmpty(sort) && sort.key === column?.accessor
      ? sort.isAsc
      : SORT_KEYS.Disabled
  )
  const columnHeaderRef = useRef<HTMLTableHeaderCellElement | null>(null)

  const toggleIsAsc = () => {
    let nextState: SORT_KEYS = SORT_KEYS.Disabled
    switch (isAsc) {
      case SORT_KEYS.Disabled:
        nextState = SORT_KEYS.Asc
        break
      case SORT_KEYS.Asc:
        nextState = SORT_KEYS.Desc
        break
      case SORT_KEYS.Desc:
        nextState = SORT_KEYS.Disabled
        break
    }
    setIsAsc(nextState)
    onSortClick(column?.accessor as string, nextState)
  }

  useEffect(() => {
    if (isAgGrid) return
    setIsAsc(
      !isEmpty(sort) && sort.key === column?.accessor
        ? sort.isAsc
        : SORT_KEYS.Disabled
    )
  }, [sort])

  return !column?.isHidden ? (
    <th
      key={column?.header}
      ref={columnHeaderRef}
      className={`text-left ${isAgGrid ? 'block' : 'border border-accent-0'} ${
        column?.type === columnTypesConst?.CHECKBOX && 'align-top'
      }`}
    >
      <GetHeaderColumn
        column={column}
        onColumnClick={onColumnClick}
        isAsc={isAsc}
        sort={sort}
        headerValues={headerValues}
        tableName={tableName}
        onFilterChange={onFilterChange}
        filterObject={filterObject}
        toggleIsAsc={toggleIsAsc}
        columnHeaderRef={columnHeaderRef}
        isFilterPresent={isFilterPresent}
        operatorRef={operatorRef}
        tabIndex={tabIndex}
        isAgGrid={isAgGrid}
      />
    </th>
  ) : null
}

export const TableRows = <T,>({
  row,
  columns,
  actions,
  rowIndex,
  onRowClick = noop,
  onColumnClick = noop,
  onActionClick = noop,
  isDisabled = false,
  isColumnChooser = false,
  tableName = '',
  noBackgroundHover = false,
  tabIndex,
  expandRowProperties,
}: TableRowPropsTypes<T>) => {
  const [rowPosition, setRowPosition] = useState<boolean>(false)
  const [isHovered, setIsHovered] = useState(false)

  const getWidthClasses = (column: TableColumnType<T>) => {
    if (
      column?.type === columnTypesConst.SELECT_BOX ||
      column?.type === columnTypesConst.CHECKBOX ||
      (column?.type === columnTypesConst.COMPONENT &&
        column?.header === columnTypesConst.CHECKBOX)
    )
      return 'auto'
    else if (column?.minWidth || column?.renderHeader) return '0'
    else return '4'
  }

  function hasExpand<T>(row: T | (T & ExpandRow)): row is T & ExpandRow {
    return typeof row === 'object' && row !== null && 'expand' in row
  }
  const isSensitiveField = (row: {
    is_client_controlled: boolean | string
  }): boolean => {
    return (
      row?.is_client_controlled === true || row?.is_client_controlled === 'true'
    )
  }
  const isSensitive =
    isSensitiveField(row as { is_client_controlled: boolean | string }) &&
    localStorage.getItem('has_sensitive_permission') !== 'true'

  return (
    <>
      <tr
        className={` bg-transparent leading-5 w-full border-b border-border-100  ${
          isDisabled
            ? 'cursor-not-allowed hover:bg-accent-100 text-accent-200'
            : noBackgroundHover
            ? ''
            : 'hover:bg-secondary-25'
        } ${rowPosition ? 'zIndexNormal' : ''}`}
        onClick={(e) => {
          e?.stopPropagation()
          onRowClick(row, rowIndex)
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {columns?.map((column: TableColumnType<T>, index) => {
          const isColumnInactive = isColumnDisabledByInactiveLookup(
            row,
            column?.accessor
          )
          const isCellDisabled = isDisabled || isColumnInactive

          return (
            <React.Fragment key={`${tableName}_row_${rowIndex}_col_${index}`}>
              {!column?.isHidden ? (
                <td
                  className={`px-4 text-left text-xs font-thin  ${
                    isCellDisabled ? DISABLED_CELL_TEXT_CLASS : ''
                  } ${
                    column?.type === columnTypesConst?.IMAGE
                      ? 'max-h-10 max-w-[5rem]'
                      : widthClasses[getWidthClasses(column)]
                  } ${column?.className ? column.className : ''} py-2.5`}
                >
                  <TableDataColumn
                    column={column}
                    row={row}
                    rowIndex={rowIndex}
                    onColumnClick={onColumnClick}
                    isDisabled={isDisabled}
                    tabIndex={tabIndex}
                  />
                </td>
              ) : null}
            </React.Fragment>
          )
        })}
        {!isEmpty(actions) ? (
          <TableActions
            tableName={tableName}
            actions={actions}
            row={row}
            rowIndex={rowIndex}
            onActionClick={onActionClick}
            isDisabled={isDisabled || isSensitive}
            setRowPosition={setRowPosition}
            isHovered={isHovered}
          />
        ) : null}
        {isColumnChooser ? <td>{null}</td> : null}
      </tr>
      {expandRowProperties &&
      expandRowProperties?.index === rowIndex &&
      hasExpand(row) ? (
        <tr>
          <td colSpan={expandRowProperties.colSpan}>
            {row[TABLE_ACTION_TYPE.EXPAND]()}
          </td>
        </tr>
      ) : (
        ''
      )}
    </>
  )
}
